import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { api } from '@/utils/api';
import { ApiErrorResponse, ApiResponse } from '@/types/api';
import { Nullable } from '@/types/helper';

import { SortType } from './useClientList';

interface GetClientTrackListRequest {
  type: 'file' | 'link';
  id: number;
  sort: SortType;
}

export interface TrackingLinkClient {
  id: number;
  isChecked: boolean;
  lastAction: Nullable<string>;
  like: boolean;
  name: Nullable<string>;
  company: Nullable<string>;
  phone: Nullable<string>;
  readCount: string;
  hashId: string;
  isShared: boolean;
  email: Nullable<string>;
  priorReadCount: string;
  isBlurred: boolean;
}

export type LockedItem = { isLocked: true };

export const isLockedItem = (
  item: TrackingLinkClient | LockedItem,
): item is LockedItem => 'isLocked' in item;

interface GetClientTrackListResponse {
  clients: (TrackingLinkClient | LockedItem)[];
}

export const getClientTrackList = ({
  type,
  id,
  sort,
}: GetClientTrackListRequest) => {
  return api.get<ApiResponse<GetClientTrackListResponse>>(
    `/client/track/list`,
    {
      params: { type, id, sort },
    },
  );
};

const useClientTrackList = <
  TData = AxiosResponse<ApiResponse<GetClientTrackListResponse>>,
  TError = AxiosError<ApiErrorResponse>,
>(
  request: GetClientTrackListRequest,
  options?: UseQueryOptions<
    AxiosResponse<ApiResponse<GetClientTrackListResponse>>,
    TError,
    TData,
    ['getClientTrackList', GetClientTrackListRequest]
  >,
): UseQueryResult<TData, TError> => {
  return useQuery(
    ['getClientTrackList', request],
    () => getClientTrackList(request),
    {
      ...options,
    },
  );
};

export default useClientTrackList;
